import { createSlice } from '@reduxjs/toolkit';
import { PaginationType } from '../../types/utils/pagination.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import snackbarUtils from '../../utils/SnackbarUtils';
import { DIDBulkRecord, DIDDetailsType, DIDE911Type, DIDsListItemType, DIDType } from '../../types/did/did.type';
import {
  deleteDIDDetails,
  getDIDAvailability,
  getDIDCSRP,
  getDIDDetails,
  getDIDDNIS,
  getDIDE911,
  getDIDRCF,
  getDIDRouting,
  getDIDsList,
  getProvisionDIDE911,
  getVerifyDIDE911,
  patchAssignmentDID,
  patchDIDCnam,
  patchDIDContract,
  patchDIDDetails,
  patchDIDE911,
  postDIDDetails,
  postDIDDNIs,
  postDIDFixRouting,
  postBulkChangeDidAssignment,
  postDIDFixService,
  postDIDRCF,
  postDIDs,
  postPBXExtensionDID,
  postTemporaryDID,
} from './dids.action';
import {
  CSRPConditionaTranslationPrefixRuleType,
  CSRPCosDestinationType,
  CSRPInformation,
} from '../../types/csrp/csrp.type';

export type DidsRouteParams = RouteParamsType & {
  provider: string;
  e911: string;
  profile: string;
  system_id: string;
  hostname: string;
  service_type_id: string;
  csrp: string;
  assignment_type: 'all' | 'pbx' | 'pri' | 'none';
  pbx_id: string;
  pri_id: string;
};

export interface DIDsState {
  didsList: null | PaginationType<DIDsListItemType>;
  didsListFetchStatus: FETCH_STATUS;
  didsRouteParams: DidsRouteParams;
  didDetails: null | DIDDetailsType;
  didDetailsFetchStatus: FETCH_STATUS;
  didDetailsPost: { success: { did: string }[]; errors: { did: string; message: string }[] } | null;
  didDetailsPostStatus: FETCH_STATUS;
  didDetailsPatchStatus: FETCH_STATUS;
  didDetailsPatchAssignmentStatus: FETCH_STATUS;
  didBulkChangeAssignmentStatus: FETCH_STATUS;
  didDetailsDeleteStatus: FETCH_STATUS;
  didFixRoutingPostStatus: FETCH_STATUS;
  didFixServicePostStatus: FETCH_STATUS;
  didRoutingFetchStatus: FETCH_STATUS;
  didRouting: { data: Record<string, unknown> } | null;
  didDNISPostStatus: FETCH_STATUS;
  didDNISFetchStatus: FETCH_STATUS;
  didDNIS: CSRPConditionaTranslationPrefixRuleType | null;
  didCSRPFetchStatus: FETCH_STATUS;
  didCSRP: CSRPInformation | null;
  didE911FetchStatus: FETCH_STATUS;
  didE911: null | DIDE911Type;
  didRCFFetchStatus: FETCH_STATUS;
  didRCFPostStatus: FETCH_STATUS;
  didRCF: CSRPCosDestinationType | null;
  didsPostStatus: FETCH_STATUS;
  didsBulk: null | Array<DIDBulkRecord>;
  didTemporaryPostStatus: FETCH_STATUS;
  didCNAMPatchStatus: FETCH_STATUS;
  didE911PatchStatus: FETCH_STATUS;
  didContractPatchStatus: FETCH_STATUS;
  verifyDIDE911FetchStatus: FETCH_STATUS;
  provisionDIDE911FetchStatus: FETCH_STATUS;
  pbxExtensionDIDPostStatus: FETCH_STATUS;
  didAvailabilityFetchStatus: FETCH_STATUS;
  didAvailability: null | { dids: Array<{ object: DIDType; did: string }>; available: boolean };
}

export const initialDidsRouteParams = {
  page: 1,
  limit: 10,
  order: '-did.id',
  q: '',
  country_id: '',
  country_category: '',
  is_system_did: 'all',
  e911: 'all',
  csrp: 'all',
  provider: '',
  profile: '',
  system_id: '',
  hostname: '',
  service_type_id: '',
  assignment_type: 'all',
  pbx_id: '',
  pri_id: '',
} as const;

const INIT_STATE: DIDsState = {
  didsRouteParams: initialDidsRouteParams,
  didsList: null,
  didsListFetchStatus: FETCH_STATUS.NULL,
  didDetails: null,
  didDetailsFetchStatus: FETCH_STATUS.NULL,
  didDetailsPostStatus: FETCH_STATUS.NULL,
  didDetailsPost: null,
  didDetailsPatchStatus: FETCH_STATUS.NULL,
  didDetailsPatchAssignmentStatus: FETCH_STATUS.NULL,
  didBulkChangeAssignmentStatus: FETCH_STATUS.NULL,
  didDetailsDeleteStatus: FETCH_STATUS.NULL,
  didFixRoutingPostStatus: FETCH_STATUS.NULL,
  didFixServicePostStatus: FETCH_STATUS.NULL,
  didRoutingFetchStatus: FETCH_STATUS.NULL,
  didDNISFetchStatus: FETCH_STATUS.NULL,
  didE911FetchStatus: FETCH_STATUS.NULL,
  didE911: null,
  didDNISPostStatus: FETCH_STATUS.NULL,
  didDNIS: null,
  didCSRPFetchStatus: FETCH_STATUS.NULL,
  didCSRP: null,
  didRCFFetchStatus: FETCH_STATUS.NULL,
  didRCFPostStatus: FETCH_STATUS.NULL,
  didRCF: null,
  didAvailabilityFetchStatus: FETCH_STATUS.NULL,
  didAvailability: null,
  didRouting: null,
  didsPostStatus: FETCH_STATUS.NULL,
  didsBulk: null,
  didTemporaryPostStatus: FETCH_STATUS.NULL,
  didCNAMPatchStatus: FETCH_STATUS.NULL,
  didE911PatchStatus: FETCH_STATUS.NULL,
  didContractPatchStatus: FETCH_STATUS.NULL,
  verifyDIDE911FetchStatus: FETCH_STATUS.NULL,
  provisionDIDE911FetchStatus: FETCH_STATUS.NULL,
  pbxExtensionDIDPostStatus: FETCH_STATUS.NULL,
};

const didsSlice = createSlice({
  name: 'dids',
  initialState: INIT_STATE,
  reducers: {
    setDIDsList: (state, action) => {
      state.didsList = action.payload;
    },
    setDIDsListFetchStatus: (state, action) => {
      state.didsListFetchStatus = action.payload;
    },
    setDIDDetails: (state, action) => {
      state.didDetails = action.payload;
    },
    setDIDDetailsFetchStatus: (state, action) => {
      state.didDetailsFetchStatus = action.payload;
    },
    setDIDDetailsPostStatus: (state, action) => {
      state.didDetailsPostStatus = action.payload;
    },
    setDIDDetailsPost: (state, action) => {
      state.didDetailsPost = action.payload;
    },
    setDIDDetailsPatchStatus: (state, action) => {
      state.didDetailsPatchStatus = action.payload;
    },
    setDIDDetailsPatchAssignmentStatus: (state, action) => {
      state.didDetailsPatchAssignmentStatus = action.payload;
    },
    setDidBulkChangeAssignmentStatus: (state, action) => {
      state.didBulkChangeAssignmentStatus = action.payload;
    },
    setDIDDetailsDeleteStatus: (state, action) => {
      state.didDetailsDeleteStatus = action.payload;
    },
    setDIDsPostStatus: (state, action) => {
      state.didsPostStatus = action.payload;
    },
    setDIDsBulk: (state, action) => {
      state.didsBulk = action.payload;
    },
    setDIDFixRoutingPostStatus: (state, action) => {
      state.didFixRoutingPostStatus = action.payload;
    },
    setDIDFixServicePostStatus: (state, action) => {
      state.didFixServicePostStatus = action.payload;
    },
    setDIDRoutingFetchStatus: (state, action) => {
      state.didRoutingFetchStatus = action.payload;
    },
    setDIDRouting: (state, action) => {
      state.didRouting = action.payload;
    },
    setDIDDNISFetchStatus: (state, action) => {
      state.didDNISFetchStatus = action.payload;
    },
    setDIDDNIS: (state, action) => {
      state.didDNIS = action.payload;
    },
    setDIDCSRPFetchStatus: (state, action) => {
      state.didCSRPFetchStatus = action.payload;
    },
    setDIDCSRP: (state, action) => {
      state.didCSRP = action.payload;
    },
    setDIDE911FetchStatus: (state, action) => {
      state.didE911FetchStatus = action.payload;
    },
    setDIDE911: (state, action) => {
      state.didE911 = action.payload;
    },
    setDIDDNISPostStatus: (state, action) => {
      state.didDNISPostStatus = action.payload;
    },
    setDIDRCFFetchStatus: (state, action) => {
      state.didRCFFetchStatus = action.payload;
    },
    setDIDRCFPostStatus: (state, action) => {
      state.didRCFPostStatus = action.payload;
    },
    setDIDRCF: (state, action) => {
      state.didRCF = action.payload;
    },
    setDIDTemporaryPostStatus: (state, action) => {
      state.didTemporaryPostStatus = action.payload;
    },
    setDIDCNAMPatchStatus: (state, action) => {
      state.didCNAMPatchStatus = action.payload;
    },
    setDIDE911PatchStatus: (state, action) => {
      state.didE911PatchStatus = action.payload;
    },
    setDIDContractPatchStatus: (state, action) => {
      state.didContractPatchStatus = action.payload;
    },
    setVerifyDIDE911FetchStatus: (state, action) => {
      state.verifyDIDE911FetchStatus = action.payload;
    },
    setProvisionDIDE911FetchStatus: (state, action) => {
      state.provisionDIDE911FetchStatus = action.payload;
    },
    setPBXExtensionDIDPostStatus: (state, action) => {
      state.pbxExtensionDIDPostStatus = action.payload;
    },
    setDIDAvailabilityFetchStatus: (state, action) => {
      state.didAvailabilityFetchStatus = action.payload;
    },
    setDIDAvailability: (state, action) => {
      state.didAvailability = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(SET_ROUTE_PARAMS, (state, action) => {
      const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
      if (payload.reducer === 'dids') {
        state.didsRouteParams = { ...state.didsRouteParams, ...payload.data };
      }
    });

    // CREATE DIDs DETAILS
    builder.addCase(postDIDs.pending, state => {
      state.didsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postDIDs.rejected, (state, action) => {
      state.didsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postDIDs.fulfilled, state => {
      state.didsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('DIDs created successfully');
      });
    });

    // CREATE DIDs DETAILS
    builder.addCase(postDIDDetails.pending, state => {
      state.didDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postDIDDetails.rejected, (state, action) => {
      state.didDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postDIDDetails.fulfilled, (state, action) => {
      state.didDetailsPostStatus = FETCH_STATUS.FULFILLED;
      state.didDetailsPost = action.payload;
    });

    // GET DIDs LIST
    builder.addCase(getDIDsList.pending, state => {
      state.didsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getDIDsList.rejected, (state, action) => {
      if (action.payload) {
        state.didsListFetchStatus = FETCH_STATUS.REJECTED;
        // state.didsList = null;
        setTimeout(() => {
          snackbarUtils.error(action.payload as string);
        });
      }
    });
    builder.addCase(getDIDsList.fulfilled, (state, action) => {
      state.didsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.didsList = action.payload;
    });

    // GET DIDs DETAILS
    builder.addCase(getDIDDetails.pending, state => {
      state.didDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getDIDDetails.rejected, (state, action) => {
      state.didDetailsFetchStatus = FETCH_STATUS.REJECTED;
      state.didDetails = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getDIDDetails.fulfilled, (state, action) => {
      state.didDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.didDetails = action.payload;
    });

    // PATCH DIDs DETAILS
    builder.addCase(patchDIDDetails.pending, state => {
      state.didDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchDIDDetails.rejected, (state, action) => {
      state.didDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchDIDDetails.fulfilled, state => {
      state.didDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('DID updated successfully');
      });
    });

    // PATCH DIDs DETAILS
    builder.addCase(patchAssignmentDID.pending, state => {
      state.didDetailsPatchAssignmentStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchAssignmentDID.rejected, (state, action) => {
      state.didDetailsPatchAssignmentStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchAssignmentDID.fulfilled, state => {
      state.didDetailsPatchAssignmentStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('DID updated successfully');
      });
    });

    // POST BULK CHANGE DID ASSIGNMENT
    builder
      .addCase(postBulkChangeDidAssignment.pending, state => {
        state.didBulkChangeAssignmentStatus = FETCH_STATUS.PENDING;
      })
      .addCase(postBulkChangeDidAssignment.rejected, (state, action) => {
        state.didBulkChangeAssignmentStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error(action.payload);
      })
      .addCase(postBulkChangeDidAssignment.fulfilled, state => {
        state.didBulkChangeAssignmentStatus = FETCH_STATUS.FULFILLED;
        snackbarUtils.info('Bulk assignment change started, the DIDs list will refresh shortly.');
      });

    // PATCH DID CONTRACT
    builder.addCase(patchDIDContract.pending, state => {
      state.didContractPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchDIDContract.rejected, (state, action) => {
      state.didContractPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchDIDContract.fulfilled, state => {
      state.didContractPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('DID contract updated successfully');
      });
    });

    // DELETE DIDs DETAILS
    builder.addCase(deleteDIDDetails.pending, state => {
      state.didDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteDIDDetails.rejected, (state, action) => {
      state.didDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteDIDDetails.fulfilled, state => {
      state.didDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('DID deleted successfully');
      });
    });

    // POST FIX DIDs ROUTING
    builder.addCase(postDIDFixRouting.pending, state => {
      state.didFixRoutingPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postDIDFixRouting.rejected, (state, action) => {
      state.didFixRoutingPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postDIDFixRouting.fulfilled, state => {
      state.didFixRoutingPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('DID has been routed successfully');
      });
    });

    // POST FIX DIDs SERVICE
    builder.addCase(postDIDFixService.pending, state => {
      state.didFixRoutingPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postDIDFixService.rejected, (state, action) => {
      state.didFixRoutingPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postDIDFixService.fulfilled, state => {
      state.didFixRoutingPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('The service has been fixed');
      });
    });

    // GET DIDs ROUTING
    builder.addCase(getDIDRouting.pending, state => {
      state.didRoutingFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getDIDRouting.rejected, (state, action) => {
      state.didRoutingFetchStatus = FETCH_STATUS.REJECTED;
      state.didRouting = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getDIDRouting.fulfilled, (state, action) => {
      state.didRoutingFetchStatus = FETCH_STATUS.FULFILLED;
      state.didRouting = action.payload;
    });

    // GET DIDs ROUTING
    builder.addCase(getDIDDNIS.pending, state => {
      state.didDNISFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getDIDDNIS.rejected, (state, action) => {
      state.didDNISFetchStatus = FETCH_STATUS.REJECTED;
      state.didDNIS = null;
    });
    builder.addCase(getDIDDNIS.fulfilled, (state, action) => {
      state.didDNISFetchStatus = FETCH_STATUS.FULFILLED;
      state.didDNIS = action.payload;
    });

    // GET DIDs ROUTING
    builder.addCase(getDIDCSRP.pending, state => {
      state.didCSRPFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getDIDCSRP.rejected, state => {
      state.didCSRPFetchStatus = FETCH_STATUS.REJECTED;
      state.didCSRP = null;
    });
    builder.addCase(getDIDCSRP.fulfilled, (state, action) => {
      state.didCSRPFetchStatus = FETCH_STATUS.FULFILLED;
      state.didCSRP = action.payload;
    });

    // GET DIDs ROUTING
    builder.addCase(getDIDE911.pending, state => {
      state.didE911FetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getDIDE911.rejected, state => {
      state.didE911FetchStatus = FETCH_STATUS.REJECTED;
      state.didE911 = null;
    });
    builder.addCase(getDIDE911.fulfilled, (state, action) => {
      state.didE911FetchStatus = FETCH_STATUS.FULFILLED;
      state.didE911 = action.payload;
    });

    // POST DID DNIS
    builder.addCase(postDIDDNIs.pending, state => {
      state.didDNISPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postDIDDNIs.rejected, (state, action) => {
      state.didDNISPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postDIDDNIs.fulfilled, state => {
      state.didDNISPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('DID DNIS updated successfully');
      });
    });

    // GET DIDs RCF
    builder.addCase(getDIDRCF.pending, state => {
      state.didRCFFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getDIDRCF.rejected, state => {
      state.didRCFFetchStatus = FETCH_STATUS.REJECTED;
      state.didRCF = null;
    });
    builder.addCase(getDIDRCF.fulfilled, (state, action) => {
      state.didRCFFetchStatus = FETCH_STATUS.FULFILLED;
      state.didRCF = action.payload;
    });

    // POST DID RCF
    builder.addCase(postDIDRCF.pending, state => {
      state.didRCFPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postDIDRCF.rejected, (state, action) => {
      state.didRCFPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postDIDRCF.fulfilled, state => {
      state.didRCFPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('DID DNIS updated successfully');
      });
    });

    // POST DIDs TEMPORARY
    builder.addCase(postTemporaryDID.pending, state => {
      state.didTemporaryPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postTemporaryDID.rejected, (state, action) => {
      state.didTemporaryPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postTemporaryDID.fulfilled, state => {
      state.didTemporaryPostStatus = FETCH_STATUS.FULFILLED;
    });

    // PATCH DID CNAM
    builder.addCase(patchDIDCnam.pending, state => {
      state.didCNAMPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchDIDCnam.rejected, (state, action) => {
      state.didCNAMPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchDIDCnam.fulfilled, state => {
      state.didCNAMPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('DID CNAM updated successfully');
      });
    });

    // PATCH DID E911
    builder.addCase(patchDIDE911.pending, state => {
      state.didE911PatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchDIDE911.rejected, (state, action) => {
      state.didE911PatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchDIDE911.fulfilled, state => {
      state.didE911PatchStatus = FETCH_STATUS.FULFILLED;
    });

    // GET VERIFY DID E911
    builder.addCase(getVerifyDIDE911.pending, state => {
      state.verifyDIDE911FetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getVerifyDIDE911.rejected, (state, action) => {
      state.verifyDIDE911FetchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getVerifyDIDE911.fulfilled, state => {
      state.verifyDIDE911FetchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('DID E911 verified successfully');
      });
    });

    // GET PROVISION DID E911
    builder.addCase(getProvisionDIDE911.pending, state => {
      state.provisionDIDE911FetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getProvisionDIDE911.rejected, (state, action) => {
      state.provisionDIDE911FetchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getProvisionDIDE911.fulfilled, state => {
      state.provisionDIDE911FetchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('The E911 address is being updated, please come back in a moment.');
      });
    });

    // POST PBX EXTENSION DID
    builder.addCase(postPBXExtensionDID.pending, state => {
      state.pbxExtensionDIDPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postPBXExtensionDID.rejected, (state, action) => {
      state.pbxExtensionDIDPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postPBXExtensionDID.fulfilled, state => {
      state.pbxExtensionDIDPostStatus = FETCH_STATUS.FULFILLED;
    });

    // GET DID AVAILABILITY
    builder.addCase(getDIDAvailability.pending, state => {
      state.didAvailabilityFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getDIDAvailability.rejected, (state, action) => {
      state.didAvailabilityFetchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getDIDAvailability.fulfilled, (state, action) => {
      state.didAvailabilityFetchStatus = FETCH_STATUS.FULFILLED;
      state.didAvailability = action.payload;
    });
  },
});

export const {
  setDIDDetailsDeleteStatus,
  setDIDDetailsFetchStatus,
  setDIDDetailsPatchStatus,
  setDIDDetailsPostStatus,
  setDIDDetails,
  setDIDsListFetchStatus,
  setDIDsPostStatus,
  setDIDsBulk,
  setDIDsList,
  setDIDRoutingFetchStatus,
  setDIDRouting,
  setDIDFixRoutingPostStatus,
  setDIDTemporaryPostStatus,
  setProvisionDIDE911FetchStatus,
  setVerifyDIDE911FetchStatus,
  setDIDE911PatchStatus,
  setDIDCNAMPatchStatus,
  setDIDContractPatchStatus,
  setDIDAvailabilityFetchStatus,
  setDIDAvailability,
  setDIDDNISFetchStatus,
  setDIDDNIS,
  setDIDDNISPostStatus,
  setDIDRCF,
  setDIDRCFFetchStatus,
  setDIDRCFPostStatus,
  setDIDDetailsPost,
  setDIDE911,
  setDIDDetailsPatchAssignmentStatus,
  setDidBulkChangeAssignmentStatus,
  setDIDE911FetchStatus,
  setDIDCSRPFetchStatus,
  setDIDCSRP,
} = didsSlice.actions;

export default didsSlice.reducer;
